import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, User, Receipt, Activity, FileCheck2, AlertCircle, Landmark } from "lucide-react";
import { API } from "./shared";
import ClientSummary from "./ClientSummary";
import ClientTransactions from "./ClientTransactions";
import ClientNotes from "./ClientNotes";
import ClientKyc from "./ClientKyc";
import ClientCashouts from "./ClientCashouts";

const TABS = [
  { id: "resumen", label: "Resumen", icon: User },
  { id: "transacciones", label: "Transacciones", icon: Receipt },
  { id: "bitacora", label: "Bitácora", icon: Activity },
  { id: "kyc", label: "Documentos KYC", icon: FileCheck2 },
  { id: "cashouts", label: "Cashouts", icon: Landmark },
];

const ClientFile = ({ appId, headers, canManage, onBack, onAppUpdated, StatusBadge }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumen");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/clients/${appId}`, { headers });
      setData(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.status === 401 ? "Sesión expirada" : "No se pudo cargar la ficha del cliente");
    }
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (!data && !error) {
    return <div className="flex items-center justify-center py-24"><Loader2 size={28} className="animate-spin text-black/30" /></div>;
  }

  const app = data?.application;

  return (
    <div data-testid="crm-client-file" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="crm-back" onClick={onBack} className="w-9 h-9 shrink-0 rounded-full bg-white border border-black/10 hover:border-black flex items-center justify-center transition-colors"><ArrowLeft size={15} /></button>
          {app && (
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-[12px] text-[#0fb013] bg-[#0fe814]/10 px-2.5 py-1 rounded-lg">{app.reference}</span>
                <h2 className="font-extrabold text-[20px] text-black truncate">{app.business?.name}</h2>
                {StatusBadge && <StatusBadge status={app.status} />}
              </div>
              <p className="text-[12px] font-medium text-black/45">{app.representative?.full_name} · {app.representative?.email} · {app.representative?.phone}</p>
            </div>
          )}
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider text-black/35">Ficha CRM del cliente</span>
      </div>

      {error && <p className="text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-2.5 rounded-xl inline-flex items-center gap-2"><AlertCircle size={14} /> {error}</p>}

      {data && (
        <>
          <div className="flex gap-1 border-b border-black/[0.08] overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              const count = t.id === "transacciones" ? data.transactions.length : t.id === "bitacora" ? data.notes.length : t.id === "kyc" ? data.kyc_docs.length : null;
              return (
                <button key={t.id} data-testid={`crm-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 text-[13px] font-bold px-4 py-3 border-b-[3px] whitespace-nowrap transition-colors ${tab === t.id ? "border-[#0fe814] text-black" : "border-transparent text-black/45 hover:text-black"}`}>
                  <Icon size={14} /> {t.label}
                  {count !== null && <span className="text-[10px] font-extrabold bg-black/[0.06] px-1.5 py-0.5 rounded-full">{count}</span>}
                </button>
              );
            })}
          </div>

          {tab === "resumen" && (
            <ClientSummary key={app.updated_at || app.id} app={app} headers={headers} canManage={canManage}
              onUpdated={(updated) => { setData((d) => ({ ...d, application: updated })); onAppUpdated?.(updated); load(); }} />
          )}
          {tab === "transacciones" && (
            <ClientTransactions appId={appId} headers={headers} canManage={canManage} transactions={data.transactions} totals={data.totals} onChange={load} />
          )}
          {tab === "bitacora" && <ClientNotes appId={appId} headers={headers} canManage={canManage} notes={data.notes} onChange={load} />}
          {tab === "kyc" && (
            <ClientKyc appId={appId} headers={headers} canManage={canManage} originalDocs={app.documents} kycDocs={data.kyc_docs} onChange={load} />
          )}
          {tab === "cashouts" && <ClientCashouts appId={appId} headers={headers} />}
        </>
      )}
    </div>
  );
};

export default ClientFile;
